import { useMemo } from 'react';
import { useWorkflowStore } from '@/state/workflow.state';
import { usePublishWorkflowNodes } from './publishWorkflowSource';
import {
  getWorkflowCommunityPublishReadiness,
  type WorkflowCommunityPublishReadiness,
} from './useWorkflowCommunityPublishReadiness.utils';

export {
  getToolCommunityPublishIsPublic,
  getToolOrgPublishIsShared,
  getWorkflowCommunityPublishReadiness,
  type WorkflowCommunityPublishBlocker,
  type WorkflowCommunityPublishCustomFontReference,
  type WorkflowCommunityPublishReadiness,
  type WorkflowCommunityPublishUnverifiedModel,
} from './useWorkflowCommunityPublishReadiness.utils';

/**
 * @param isPublicOverride replaces the workflow's visibility in the `not_public` gate — pass the
 * tool's own effective access for a Tool publish, or omit it for a workflow publish.
 */
export function useWorkflowCommunityPublishReadiness(isPublicOverride?: boolean): WorkflowCommunityPublishReadiness {
  // Graph-optional: the status banner also renders on `/tool`, which has no FlowContext. There the
  // Share modal provides the workflow to its publish flow; the banner gets none and reads the blocker
  // for analytics only.
  // TODO: this is a short-term crash fix. Readiness only matters for the `updates_available` publish
  // CTA, so on the `/tool` banner we compute a throwaway result (no nodes, empty workflow store) just
  // to report a blocker. After release, split `updates_available` into its own component that keeps a
  // strict `useNodes()`, leave the status-only banner off the publish-gating hooks, and drop
  // `useOptionalNodes`.
  const nodes = usePublishWorkflowNodes();
  const recipeVisibility = useWorkflowStore((state) => state.recipe.visibility);
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);

  return useMemo(
    () =>
      getWorkflowCommunityPublishReadiness({
        isPublicOverride,
        nodes,
        nodeTypes,
        visibility: recipeVisibility,
      }),
    [isPublicOverride, nodeTypes, nodes, recipeVisibility],
  );
}
